import PropTypes from "prop-types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/**
 * A labelled text field for Settings: the typed text is the value (a
 * decimal keypad by default — never type=number, which eats "62,"), the
 * unit sits after it and is read as part of its description, the helper
 * says what to type, and an error (the form's or the server's) marks it
 * invalid and is read with it.
 *
 * `subgrid` (a field in a grid of fields, without a helper): the field
 * takes three of its parent grid's row tracks — label, input, error — so
 * when a neighbour's label wraps onto two lines, every input in the row
 * still lines up; the label sits at the bottom of its track, against its
 * input. The parent must be a grid.
 */
function SettingsField({
  id,
  label,
  value,
  onChange,
  unit,
  helper,
  error,
  inputMode = "decimal",
  placeholder,
  maxLength,
  subgrid = false,
  className,
  inputClassName,
}) {
  const describedBy = [unit && `${id}-unit`, error && `${id}-error`, helper && `${id}-help`]
    .filter(Boolean)
    .join(" ");
  return (
    <div
      className={cn(
        subgrid ? "row-span-3 grid min-w-0 grid-rows-subgrid gap-y-1.5" : "flex min-w-0 flex-col gap-1.5",
        className,
      )}
    >
      <Label htmlFor={id} className={cn(subgrid && "self-end")}>
        {label}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="text"
          inputMode={inputMode}
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="done"
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={cn("h-11 md:h-9", inputClassName)}
        />
        {unit && (
          <span id={`${id}-unit`} className="shrink-0 text-sm text-muted-foreground">
            {unit}
          </span>
        )}
      </div>
      {error && (
        <p id={`${id}-error`} className="m-0 text-sm text-destructive">
          {error}
        </p>
      )}
      {helper && (
        <p id={`${id}-help`} className="m-0 text-xs text-muted-foreground">
          {helper}
        </p>
      )}
    </div>
  );
}

SettingsField.propTypes = {
  id: PropTypes.string.isRequired,
  /** Shown and spoken; may carry an sr-only part ("Assist for band 2"). */
  label: PropTypes.node.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  unit: PropTypes.string,
  helper: PropTypes.node,
  error: PropTypes.string,
  inputMode: PropTypes.oneOf(["decimal", "numeric", "text"]),
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  /** Lay it on the parent grid's rows (label, input, error), aligned with its neighbours. */
  subgrid: PropTypes.bool,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
};

export default SettingsField;
